import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Card } from '@els/els-react--card';
import { ButtonWithIcon } from '@els/els-react--button';

import './ResultDetailCard.scss';

/**
 * Leyden card used for an opened detail category, shared across result types.
 * Mirrors the Substance details card: the title and a close button live in the
 * card heading, with the category content rendered below (vertical layout).
 *
 * `hideHeading` renders the card with no heading (no title, no close) — used by
 * Reaction results, where the top Hits/Conditions toggle already owns open/close.
 */
function ResultDetailCard({ id, title, onClose, className, closeDataE2e, children, hideHeading = false, size }) {
  const headingId = `${id}-heading`;

  const headingProps = hideHeading
    ? {}
    : {
        heading: (
          <div className="result-detail-card__header">
            <h3 id={headingId} className="result-detail-card__title">
              {title}
            </h3>
            <ButtonWithIcon
              data-e2e={closeDataE2e}
              type="borderless"
              size="x-small"
              sprite="Close"
              iconRight
              iconOnly
              className="e2e-close"
              aria-label={`Close ${title}`}
              onClick={onClose}>
              Close {title}
            </ButtonWithIcon>
          </div>
        ),
        headingLevel: 'h3',
        // RX-23573: no `role="region"` landmark here. This card renders once per opened detail
        // category per result row, so a region landmark multiplied into dozens of identical
        // landmarks (Abstract, Index Terms, etc.) — landmark overuse (ARIA APG). The `<h3>` heading
        // keeps each panel reachable via the heading rotor, which is the intended mechanism.
        'aria-labelledby': headingId,
      };

  return (
    <Card size={size || 'x-small'} className={classnames('result-detail-card', className)} id={id} {...headingProps}>
      {children}
    </Card>
  );
}

ResultDetailCard.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  onClose: PropTypes.func,
  className: PropTypes.string,
  closeDataE2e: PropTypes.string,
  children: PropTypes.node,
  hideHeading: PropTypes.bool,
};

export default ResultDetailCard;
